import { Asset } from '@promoboxx/graphql-gateway-types'
import { useHistory, useLocation } from 'react-router'

const SHARE_ASSET_ID = 'shareAssetId'
const SHARE_ASSET_TYPE = 'shareAssetType'

export type ShareDialogEvent =
  | 'shareAssetDialogOpened'
  | 'shareAssetDialogClosed'
  | 'shareAssetDialogUpdated'

export type ShareAssetDialogEventData = {
  id: Asset['id']
  type: string
}

export function buildShareAssetDialogHref(id: Asset['id'], type: string) {
  if (!type) {
    throw new Error('channel is required')
  }
  const params = new URLSearchParams(window.location.search)
  params.set(SHARE_ASSET_ID, id)
  params.set(SHARE_ASSET_TYPE, type)

  return `${window.location.pathname}?${params.toString()}`
}

export function useShareAssetDialog() {
  const history = useHistory()
  const location = useLocation()

  function subscribe(eventName: ShareDialogEvent, cb: () => void) {
    document.addEventListener(eventName, cb)
  }

  function unsubscribe(eventName: ShareDialogEvent, cb: () => void) {
    document.removeEventListener(eventName, cb)
  }

  function publish(
    eventName: ShareDialogEvent,
    data?: ShareAssetDialogEventData,
  ) {
    const event = new CustomEvent(eventName, { detail: data })
    document.dispatchEvent(event)
  }

  function getShareAssetParams() {
    const params = new URLSearchParams(location.search)
    const id = params.get(SHARE_ASSET_ID)
    const type = params.get(SHARE_ASSET_TYPE)

    return { id, type }
  }

  function openShareAssetDialog(id: Asset['id'], type: string) {
    const url = buildShareAssetDialogHref(id, type)
    history.push(url)
    publish('shareAssetDialogOpened', { id, type })
  }

  function closeShareAssetDialog() {
    const params = new URLSearchParams(window.location.search)
    const queryParams = getShareAssetParams()
    params.delete(SHARE_ASSET_ID)
    params.delete(SHARE_ASSET_TYPE)

    history.push(`${window.location.pathname}?${params.toString()}`)
    publish('shareAssetDialogClosed', {
      id: queryParams?.id || '',
      type: queryParams.type || '',
    })
  }

  return {
    subscribe,
    unsubscribe,
    publish,
    getShareAssetParams,
    closeShareAssetDialog,
    openShareAssetDialog,
  }
}
